// MANAGER TASKS (Module 09, P3-E PR-E2, D-292 rulings 1-4; P3-E2 PR E2-1,
// D-313) — the pieces the `/sales/tasks` list, the open-task card on the
// order, the quote, the customer and the deal, and the side peeks share, so no
// two surfaces word the same task two ways or offer it different verbs.
//
// A COMPONENT FILE, NOT A SCREEN EXPORT: the order detail, the composer, the
// 360 and the peeks import the card from here, never from screens/tasks.tsx —
// one screen importing another pulls the whole other screen into its chunk
// (the reason components/customer-notifications.tsx exists).
//
// A SYSTEM TASK CARRIES NO PROSE ON THE WIRE (D-292/3, D-225). The engine
// writes `origin` + `reason_code` + a subject and nothing else, so the words a
// manager reads — "Ready order not collected", "Customer has no messaging
// channel" — exist only here and in the dictionary. A manual task carries its
// author's own title, verbatim and untranslated, and only a manual task's
// words can be edited (the PATCH answers 400 `system_task_words` otherwise).
//
// EVERY VERB IS OFFERED ONLY WHERE IT WOULD PASS. Complete, Move to another
// day and Edit belong to the assignee or a literal admin, Reassign to a
// literal admin, Withdraw to a literal admin or the assignee who wrote the
// task for themselves (spec 09 §4.12, §7 narrowings 11 and 17). The client
// cannot compare user ids (hooks/activity-queries.ts says why), so a row is
// the reader's own only when the server returned it under `scope=mine`. A
// reader who is neither gets no button, rather than a button that answers 403.
//
// THE FOUR NEW VERBS ARE DIALOGS, one per verb, each sending one request that
// names only what changed (D-313 ruling 1: one PATCH, one audit row). A body
// that would change nothing is never sent; the dialog's confirm stays off
// until something differs.
//
// THE DONE CELL (P3-E2 PR E2-2d, spec 09 §4.12's E2-2d annotation, D-326
// ruling 5). The task list's Done column and the 360's Tasks panel edit the
// task in place (design-system §3.13): one press is `/complete`, on Complete's
// own gate. Done is one way, as the route is, so a done row's cell is read-only
// and so is a cell this surface has just completed, before the refetch moves
// the row. Complete stays in the row's verbs, because a phone renders no
// editor.

import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { ToggleCellEditor } from "../../../../components/cell-editor";
import type { TFunction } from "i18next";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { RowMenu } from "../../../../components/row-menu";
import { Card } from "../../../../components/card";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { Modal } from "../../../../components/modal";
import { RefLabel } from "../../../../components/ref-label";
import { Select } from "../../../../components/select";
import { CardSkeleton, ErrorState, OfflineState } from "../../../../components/states";
import type { ChipVariant } from "../../../../components/status-chip";
import { client } from "../../../../lib/api";
import { facilityTodayIso } from "../../../../lib/business-dates";
import { formatBusinessDate, formatTimestamp } from "../../../../lib/datetime";
import { UnauthorizedError } from "../../../../lib/problem";
import { useTenantTimezone } from "../../../../lib/role-codes";
import { RequestError, requestIdOf, unwrap, type FieldError } from "../../../../lib/request";
import { useDebouncedValue } from "../../../../lib/use-debounced-value";
import { MutationFailureNote } from "../mutation-failure-note";
import {
  ACTIVITY_KINDS,
  canCompleteTask,
  canEditTaskWords,
  canMoveTask,
  canReassignTask,
  canWithdrawTask,
  useCompleteActivity,
  useCreateActivity,
  useDocumentTasks,
  useRecentDoneTasks,
  useUpdateActivity,
  useWithdrawActivity,
  type SalesActivity,
  type SalesActivityCreateRequest,
  type SalesActivityKind,
  type SalesActivityUpdateRequest,
  type TaskSubject,
} from "../hooks/activity-queries";
import { useOpportunities } from "../hooks/opportunity-queries";
import { deriveSalesPermissions, useSalesPermissions } from "../hooks/permissions";
import { salesKeys, useCustomers } from "../hooks/sales-queries";
import { useOrders, useQuotes } from "../hooks/quote-queries";

// ---------------------------------------------------------------------------
// the words
// ---------------------------------------------------------------------------

/** The reason codes this screen has a sentence for — the wire enum. Any other
 * value (an old bundle meeting a newer server) reads the general sentence,
 * never a code. */
export const TASK_REASONS: ReadonlySet<string> = new Set([
  "READY_NOT_COLLECTED",
  "QUOTE_NO_ANSWER",
  "CUSTOMER_OPTED_OUT",
  "NO_CHANNEL",
]);

export function taskReasonKey(reason: string | null): string {
  return reason !== null && TASK_REASONS.has(reason)
    ? `sales.tasks.reason.${reason}`
    : "sales.tasks.reason.other";
}

/** What a task asks for: a manual task's own title, verbatim; a system task's
 * reason, in words (D-292/3). */
export function taskWhat(t: TFunction, row: Pick<SalesActivity, "title" | "reason_code">): string {
  if (row.title !== null && row.title.trim().length > 0) return row.title;
  return t(taskReasonKey(row.reason_code));
}

/** The origin chip. A task the system created is amber — a customer was not
 * reached and somebody has to act; a hand-made one is neutral. */
export function taskOriginVariant(origin: SalesActivity["origin"]): ChipVariant {
  return origin === "manual" ? "neutral" : "warn";
}

/** The status chip — the product-wide law: done is green, an open task is the
 * accent (it is work in hand), a closed one is neutral (nobody did it). */
export function taskStatusVariant(status: SalesActivity["status"]): ChipVariant {
  switch (status) {
    case "done":
      return "ok";
    case "open":
      return "accent";
    case "cancelled":
      return "neutral";
  }
}

/** The status word. A cancelled task is one of two closes since E2-1 (spec 09
 * §4.12, D-313 ruling 2): the engine's automatic close, or a person's
 * withdrawal, which reads "Withdrawn" beside it in the closed list. */
export function taskStatusKey(row: Pick<SalesActivity, "status" | "cancel_reason">): string {
  if (row.status === "cancelled" && row.cancel_reason === "WITHDRAWN") {
    return "sales.tasks.withdrawn";
  }
  switch (row.status) {
    case "open":
      return "sales.tasks.state.open";
    case "done":
      return "sales.tasks.state.done";
    case "cancelled":
      return "sales.tasks.state.cancelled";
  }
}

/** Is this row's due date to be painted overdue? The SERVER decides `overdue`
 * against the tenant's business today (the client clock is never asked), and
 * only an open task can be late. */
export function isTaskOverdue(row: Pick<SalesActivity, "overdue" | "status">): boolean {
  return row.status === "open" && row.overdue;
}

/** The refusal sentence for a failed Complete, by the server's code. */
export function completeFailureText(t: TFunction, error: unknown): string {
  if (error instanceof RequestError) {
    if (error.code === "ACTIVITY_NOT_OPEN") return t("sales.tasks.notOpen");
    if (error.code === "ACTIVITY_NOT_FOUND") return t("sales.tasks.notFound");
    if (error.httpStatus === 403) return t("sales.tasks.notYours");
  }
  return t("sales.tasks.completeFailed");
}

/**
 * The refusal sentence's KEY for a failed change (a move, an edit or a
 * reassign; pure, unit-tested), in the PATCH's own refusal order (spec 09 §5):
 * 404, 403, 409, 400 `system_task_words`, 422. A 400 that names a field is the
 * form's to say on the field, so it falls to the verb's own fallback here.
 */
export function changeFailureKey(error: unknown, fallbackKey: string): string {
  if (error instanceof RequestError) {
    if (error.code === "ACTIVITY_NOT_FOUND") return "sales.tasks.notFound";
    if (error.httpStatus === 403) return "sales.tasks.changeNotYours";
    if (error.code === "ACTIVITY_NOT_OPEN") return "sales.tasks.notOpen";
    if (error.fieldErrors.some((fe) => fe.code === "system_task_words")) {
      return "sales.tasks.systemWords";
    }
    if (error.code === "ACTIVITY_ASSIGNEE_NOT_ELIGIBLE") return "sales.tasks.assigneeNotEligible";
  }
  return fallbackKey;
}

/** The refusal sentence's KEY for a failed Withdraw (pure, unit-tested), in
 * the route's order: 404, 403, 409 `ACTIVITY_NOT_WITHDRAWABLE`, 409
 * `ACTIVITY_NOT_OPEN`. */
export function withdrawFailureKey(error: unknown): string {
  if (error instanceof RequestError) {
    if (error.code === "ACTIVITY_NOT_FOUND") return "sales.tasks.notFound";
    if (error.httpStatus === 403) return "sales.tasks.withdrawNotYours";
    if (error.code === "ACTIVITY_NOT_WITHDRAWABLE") return "sales.tasks.notWithdrawable";
    if (error.code === "ACTIVITY_NOT_OPEN") return "sales.tasks.notOpen";
  }
  return "sales.tasks.withdrawFailed";
}

// ---------------------------------------------------------------------------
// the cells
// ---------------------------------------------------------------------------

/** The task's subject — customer · deal · quote · order — each a link to its
 * own screen. A label the reader's door refuses is served null (D-312 ruling
 * 21) and renders as the short id, never blank. A null label therefore proves
 * that the record's page would refuse this reader, so that subject is text;
 * and a subject whose screen the reader's own gate refuses is text too
 * (`canOpenCustomers`, `canOpenPipeline` for §7 narrowing 15, `canOpenQuotes`,
 * `canOpenOrders`), because a link into a refusal is the D-088 defect. */
export function TaskSubjectLinks({ row }: { readonly row: SalesActivity }) {
  const { t } = useTranslation();
  const perms = useSalesPermissions();
  const parts: ReactNode[] = [];
  if (row.customer_id !== null) {
    const label = <RefLabel value={row.customer_name ?? undefined} id={row.customer_id} />;
    parts.push(
      row.customer_name !== null && perms.canOpenCustomers ? (
        <Link
          key="customer"
          to="/sales/customers/$customerId"
          params={{ customerId: row.customer_id }}
          className="ds-lnk"
          data-testid="sales-task-customer"
        >
          {label}
        </Link>
      ) : (
        <span key="customer" data-testid="sales-task-customer">
          {label}
        </span>
      ),
    );
  }
  if (row.opportunity_id !== null) {
    const label = <RefLabel value={row.opportunity_no ?? undefined} id={row.opportunity_id} />;
    parts.push(
      row.opportunity_no !== null && perms.canOpenPipeline ? (
        <Link
          key="deal"
          to="/sales/opportunities/$opportunityId"
          params={{ opportunityId: row.opportunity_id }}
          className="ds-lnk"
          data-testid="sales-task-deal"
          title={row.opportunity_title ?? undefined}
        >
          {label}
        </Link>
      ) : (
        <span key="deal" data-testid="sales-task-deal">
          {label}
        </span>
      ),
    );
  }
  if (row.quote_id !== null) {
    const label = <RefLabel value={row.quote_no ?? undefined} id={row.quote_id} />;
    parts.push(
      row.quote_no !== null && perms.canOpenQuotes ? (
        <Link
          key="quote"
          to="/sales/quotes/$quoteId"
          params={{ quoteId: row.quote_id }}
          className="ds-lnk"
          data-testid="sales-task-quote"
        >
          {label}
        </Link>
      ) : (
        <span key="quote" data-testid="sales-task-quote">
          {label}
        </span>
      ),
    );
  }
  if (row.sales_order_id !== null) {
    const label = <RefLabel value={row.order_no ?? undefined} id={row.sales_order_id} />;
    parts.push(
      row.order_no !== null && perms.canOpenOrders ? (
        <Link
          key="order"
          to="/sales/orders/$orderId"
          params={{ orderId: row.sales_order_id }}
          className="ds-lnk"
          data-testid="sales-task-order"
        >
          {label}
        </Link>
      ) : (
        <span key="order" data-testid="sales-task-order">
          {label}
        </span>
      ),
    );
  }
  if (parts.length === 0) {
    return <span className="sales-hint">{t("sales.tasks.subjectNone")}</span>;
  }
  // The toolbar's flex gap separates the links; a typed "·" separator wrapped
  // onto the NEXT line at desk width in Russian and led it with a stray dot.
  return <span className="sales-toolbar">{parts}</span>;
}

/** What the task asks for, with its kind beside a manual title. */
export function TaskWhat({ row }: { readonly row: SalesActivity }) {
  const { t } = useTranslation();
  return (
    <span data-testid="sales-task-what" data-reason={row.reason_code ?? undefined}>
      {taskWhat(t, row)}
      {row.origin === "manual" ? (
        <span className="sales-provisional">{t(`sales.tasks.kind.${row.kind}`)}</span>
      ) : null}
    </span>
  );
}

/** The due date — RED when the server says the task is overdue, and said in
 * a word as well, because a colour alone is no signal to a screen reader. */
export function TaskDue({ row }: { readonly row: SalesActivity }) {
  const { t, i18n } = useTranslation();
  const overdue = isTaskOverdue(row);
  return (
    <span
      className={overdue ? "sales-valid-urgent" : undefined}
      data-testid="sales-task-due"
      data-overdue={overdue ? "true" : "false"}
    >
      {formatBusinessDate(row.due_on, i18n.language)}
      {overdue ? <span className="visually-hidden"> {t("sales.tasks.overdue")}</span> : null}
    </span>
  );
}

export function TaskAssignee({ row }: { readonly row: SalesActivity }) {
  return <RefLabel value={row.assignee_name ?? undefined} id={row.assignee_user_id} kind="user" />;
}

/** When a closed task closed: "Done …", or, for a person's withdrawal, the
 * moment alone, because the status chip beside it already says Withdrawn. All
 * in the tenant zone. The engine's close keeps its reason line. */
export function TaskClosedAt({ row }: { readonly row: SalesActivity }) {
  const { t, i18n } = useTranslation();
  const timeZone = useTenantTimezone();
  if (row.status === "done" && row.done_at !== null) {
    return (
      <span className="sales-provisional">
        {t("sales.tasks.doneAt", { when: formatTimestamp(row.done_at, i18n.language, timeZone) })}
      </span>
    );
  }
  if (row.status === "cancelled" && row.cancel_reason === "WITHDRAWN") {
    return row.cancelled_at === null ? null : (
      <span className="sales-provisional num" data-testid="sales-task-withdrawn-at">
        {formatTimestamp(row.cancelled_at, i18n.language, timeZone)}
      </span>
    );
  }
  if (row.status === "cancelled" && row.cancel_reason !== null) {
    return (
      <span className="sales-hint" data-testid="sales-task-cancel-reason">
        {t(`sales.tasks.cancelReason.${row.cancel_reason}`)}
      </span>
    );
  }
  return null;
}

// ---------------------------------------------------------------------------
// the create form
// ---------------------------------------------------------------------------

/** The subject a create is fixed to when it is opened FROM a record. */
export interface TaskPrefill {
  readonly customerId: string | null;
  readonly quoteId?: string | undefined;
  readonly salesOrderId?: string | undefined;
  /** A deal (P3-E2 PR E2-1, D-313 ruling 5): the deal page's Add task and
   * the board's Schedule. */
  readonly opportunityId?: string | undefined;
  /** The record's number or name, for the fixed-subject line. */
  readonly label: string;
}

/** The create and edit forms' values, as typed. */
export interface TaskDraft {
  readonly kind: SalesActivityKind;
  readonly title: string;
  readonly note: string;
  readonly dueOn: string;
}

export type TaskDraftField = "title" | "note" | "due_on";

/** Client-side checks, the contract's own bounds (pure, unit-tested). */
export function validateTaskDraft(draft: TaskDraft): Partial<Record<TaskDraftField, string>> {
  const errors: Partial<Record<TaskDraftField, string>> = {};
  const title = draft.title.trim();
  if (title.length === 0) errors.title = "sales.tasks.titleRequired";
  else if (title.length > 200) errors.title = "sales.tasks.titleTooLong";
  if (draft.note.trim().length > 2000) errors.note = "sales.tasks.noteTooLong";
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(draft.dueOn)) errors.due_on = "sales.tasks.dueRequired";
  return errors;
}

/** The server's per-field validation, mapped onto the form's own sentences
 * (VALIDATION_FAILED `errors[].path` names the body key). */
export function serverTaskFieldErrors(
  fieldErrors: readonly FieldError[],
): Partial<Record<TaskDraftField, string>> {
  const errors: Partial<Record<TaskDraftField, string>> = {};
  for (const fe of fieldErrors) {
    const key = fe.path.split(".").pop() ?? fe.path;
    if (key === "title") {
      errors.title =
        fe.code === "too_big" ? "sales.tasks.titleTooLong" : "sales.tasks.titleRequired";
    } else if (key === "note") {
      errors.note = "sales.tasks.noteTooLong";
    } else if (key === "due_on") {
      errors.due_on = "sales.tasks.dueRequired";
    }
  }
  return errors;
}

const SUBJECT_NOT_FOUND = new Set(["CUSTOMER_NOT_FOUND", "QUOTE_NOT_FOUND", "ORDER_NOT_FOUND"]);

/** The subject's refusal sentence, if the create's failure is about the
 * subject (pure, unit-tested): an invisible record's not-found, or the 400
 * whose field error says the subjects disagree. The deal has its own pair
 * (E2-1): a deal the reader cannot see, and a deal of another customer or a
 * quote on another deal (`subject_mismatch` at `opportunity_id`). */
export function subjectErrorKey(error: unknown): string | undefined {
  if (!(error instanceof RequestError)) return undefined;
  if (error.code === "OPPORTUNITY_NOT_FOUND") return "sales.tasks.subjectNotFoundDeal";
  if (error.code !== undefined && SUBJECT_NOT_FOUND.has(error.code)) {
    return "sales.tasks.subjectNotFound";
  }
  if (error.httpStatus === 400) {
    const mismatch = error.fieldErrors.find((fe) => fe.code === "subject_mismatch");
    if (mismatch !== undefined) {
      return mismatch.path.split(".").pop() === "opportunity_id"
        ? "sales.tasks.subjectMismatchDeal"
        : "sales.tasks.subjectMismatch";
    }
  }
  return undefined;
}

/** One row of `GET /v1/members`, as far as the picker reads it. */
export interface AssigneeMember {
  readonly user_id: string;
  readonly display_name: string;
  readonly locked: boolean;
  readonly memberships: readonly { readonly role_code: string; readonly status: string }[];
}

/**
 * The members a task may be assigned to, by name (pure, unit-tested).
 *
 * An eligible assignee holds `sales.activity.write` (D-292 ruling 2, as
 * amended by the E2 audit's G1): the people who can open the task list and
 * complete what lands on it — ga, fa and sales. A planner, finance, qc or
 * storekeeper reader holds `sales.read` alone; a task handed to one would sit
 * open for ever, invisible on their glass and refused at Complete. So the
 * picker derives each member's grants from their ACTIVE memberships through
 * this area's mirror (`deriveSalesPermissions`, SALES_ROLE_GRANTS), exactly as
 * every other gate here does. A custom role derives nothing and is not
 * offered — the mirror cannot see its grants (fail-closed, the area's rule);
 * a locked account is not offered either. The server re-checks regardless
 * (422 ACTIVITY_ASSIGNEE_NOT_ELIGIBLE), including the case the client cannot
 * see: a sales assignee who cannot read the task's customer, document or deal.
 */
export function eligibleAssignees(
  members: readonly AssigneeMember[],
): { readonly id: string; readonly name: string }[] {
  return members
    .filter((member) => !member.locked)
    .filter((member) =>
      deriveSalesPermissions(
        new Set(member.memberships.filter((m) => m.status === "active").map((m) => m.role_code)),
      ).has("sales.activity.write"),
    )
    .map((member) => ({ id: member.user_id, name: member.display_name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Active members a task may be assigned to — the admin's picker at create
 * and at Reassign. The same read (and cache entry) as the owner index
 * (`GET /v1/members`, `identity.view_members`, which only the admins hold). */
export function useAssigneeCandidates(enabled: boolean) {
  const query = useQuery({
    queryKey: salesKeys.members(),
    enabled,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    retry: false,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/members", { signal, params: { query: { limit: 200 } } }),
        "members",
      ),
  });
  return {
    candidates: eligibleAssignees(query.data?.items ?? []),
    isPending: enabled && query.isPending,
  };
}

/** The create body's subject, from a fixed prefill or the form's picks (pure,
 * unit-tested). Only the ids that are set are named. */
export function createSubjectBody(
  subject:
    | { readonly kind: "fixed"; readonly prefill: TaskPrefill }
    | {
        readonly kind: "picked";
        readonly customerId: string;
        readonly quoteId: string;
        readonly orderId: string;
        readonly opportunityId: string;
      },
): Pick<
  SalesActivityCreateRequest,
  "customer_id" | "quote_id" | "sales_order_id" | "opportunity_id"
> {
  if (subject.kind === "fixed") {
    const p = subject.prefill;
    return {
      ...(p.customerId !== null ? { customer_id: p.customerId } : {}),
      ...(p.quoteId !== undefined ? { quote_id: p.quoteId } : {}),
      ...(p.salesOrderId !== undefined ? { sales_order_id: p.salesOrderId } : {}),
      ...(p.opportunityId !== undefined ? { opportunity_id: p.opportunityId } : {}),
    };
  }
  return {
    ...(subject.customerId.length > 0 ? { customer_id: subject.customerId } : {}),
    ...(subject.quoteId.length > 0 ? { quote_id: subject.quoteId } : {}),
    ...(subject.orderId.length > 0 ? { sales_order_id: subject.orderId } : {}),
    ...(subject.opportunityId.length > 0 ? { opportunity_id: subject.opportunityId } : {}),
  };
}

/** How many of a customer's open deals the create form's picker lists. A
 * customer carries a few open deals at most. */
export const DEAL_PICKER_LIMIT = 50;

export function NewTaskModal({
  prefill,
  onClose,
  onCreated,
}: {
  readonly prefill?: TaskPrefill | undefined;
  readonly onClose: () => void;
  readonly onCreated?: ((row: SalesActivity) => void) | undefined;
}) {
  const { t } = useTranslation();
  const perms = useSalesPermissions();
  const timeZone = useTenantTimezone();
  const create = useCreateActivity();

  // Due defaults to the TENANT's today (D-089: a business date a client puts
  // into a default is the plant's calendar, never the viewer's).
  const [draft, setDraft] = useState<TaskDraft>(() => ({
    kind: "todo",
    title: "",
    note: "",
    dueOn: facilityTodayIso(Date.now(), timeZone),
  }));
  // The zone arrives with the role feed; a form opened before it settles
  // re-seeds its default once — never after the reader has touched the date.
  const [dueTouched, setDueTouched] = useState(false);
  useEffect(() => {
    if (!dueTouched && timeZone !== undefined) {
      setDraft((prior) => ({ ...prior, dueOn: facilityTodayIso(Date.now(), timeZone) }));
    }
  }, [timeZone, dueTouched]);

  const [submitted, setSubmitted] = useState(false);
  const [assignee, setAssignee] = useState("");
  const [customerText, setCustomerText] = useState("");
  const customerQ = useDebouncedValue(customerText.trim(), 250);
  const [customerId, setCustomerId] = useState("");
  const [quoteId, setQuoteId] = useState("");
  const [orderId, setOrderId] = useState("");
  const [opportunityId, setOpportunityId] = useState("");

  const fixed = prefill !== undefined;
  const customerMatches = useCustomers({ q: customerQ }, undefined, !fixed && customerQ.length > 1);
  const pickedCustomer = !fixed && customerId.length > 0;
  const customerQuotes = useQuotes(
    { customer_id: customerId, limit: 50 },
    undefined,
    pickedCustomer,
  );
  const customerOrders = useOrders(
    { customer_id: customerId, limit: 50 },
    undefined,
    pickedCustomer,
  );
  // The deal picker (E2-1) is offered to a reader of the pipeline alone:
  // narrowing 15 refuses every other lineage the deal read, and a picker that
  // can only be refused is the D-088 defect.
  const offerDeals = pickedCustomer && perms.canOpenPipeline;
  // The customer's OPEN deals (an absent `status` is open), on the deal
  // list's own read and key, so a deal write anywhere refreshes the picker.
  const customerDeals = useOpportunities(
    { customer_id: customerId, limit: DEAL_PICKER_LIMIT },
    undefined,
    offerDeals,
  );
  const assignees = useAssigneeCandidates(perms.isAdmin);

  const clientErrors = validateTaskDraft(draft);
  const serverErrors =
    create.error instanceof RequestError && create.error.httpStatus === 400
      ? serverTaskFieldErrors(create.error.fieldErrors)
      : {};
  const fieldError = (field: TaskDraftField): string | undefined =>
    (submitted ? clientErrors[field] : undefined) ?? serverErrors[field];
  const assigneeRefused =
    create.error instanceof RequestError && create.error.code === "ACTIVITY_ASSIGNEE_NOT_ELIGIBLE";
  // The subject's refusals: a named record the caller cannot see (its own
  // not-found), and subjects that disagree (400 `subject_mismatch`). Both land
  // on the subject, never as a blank or a generic note.
  const subjectKey = subjectErrorKey(create.error);
  const otherFailure =
    create.isError &&
    !assigneeRefused &&
    subjectKey === undefined &&
    !(
      create.error instanceof RequestError &&
      create.error.httpStatus === 400 &&
      Object.keys(serverErrors).length > 0
    );

  const submit = () => {
    setSubmitted(true);
    if (Object.keys(clientErrors).length > 0) return;
    const note = draft.note.trim();
    const body: SalesActivityCreateRequest = {
      kind: draft.kind,
      title: draft.title.trim(),
      due_on: draft.dueOn,
      ...(note.length > 0 ? { note } : {}),
      ...createSubjectBody(
        fixed
          ? { kind: "fixed", prefill }
          : { kind: "picked", customerId, quoteId, orderId, opportunityId },
      ),
      ...(perms.isAdmin && assignee.length > 0 ? { assignee_user_id: assignee } : {}),
    };
    create.mutate(body, {
      onSuccess: (row) => {
        onCreated?.(row);
        onClose();
      },
    });
  };

  const errorLine = (field: TaskDraftField) => {
    const key = fieldError(field);
    return key === undefined ? null : (
      <span className="field-error" role="alert" data-testid={`sales-task-error-${field}`}>
        {t(key)}
      </span>
    );
  };

  return (
    <Modal
      heading={t("sales.tasks.createTitle")}
      label={t("sales.tasks.createTitle")}
      closeLabel={t("sales.common.close")}
      onClose={onClose}
      className="sales-modal"
      testId="sales-task-modal"
      pending={create.isPending}
    >
      <div className="sales-form">
        <Field label={t("sales.tasks.kindLabel")}>
          <Select
            value={draft.kind}
            data-testid="sales-task-kind"
            onChange={(e) => {
              setDraft({ ...draft, kind: e.target.value as SalesActivityKind });
            }}
          >
            {ACTIVITY_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {t(`sales.tasks.kind.${kind}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("sales.tasks.titleLabel")} wide>
          <input
            value={draft.title}
            data-testid="sales-task-title"
            aria-invalid={fieldError("title") !== undefined ? true : undefined}
            onChange={(e) => {
              setDraft({ ...draft, title: e.target.value });
            }}
          />
          {errorLine("title")}
        </Field>
        <Field label={t("sales.tasks.dueLabel")}>
          <input
            type="date"
            value={draft.dueOn}
            data-testid="sales-task-due-input"
            aria-invalid={fieldError("due_on") !== undefined ? true : undefined}
            onChange={(e) => {
              setDueTouched(true);
              setDraft({ ...draft, dueOn: e.target.value });
            }}
          />
          {errorLine("due_on")}
        </Field>
        {perms.isAdmin ? (
          <Field label={t("sales.tasks.assigneeLabel")}>
            <Select
              value={assignee}
              data-testid="sales-task-assignee"
              aria-invalid={assigneeRefused ? true : undefined}
              onChange={(e) => {
                create.reset();
                setAssignee(e.target.value);
              }}
            >
              <option value="">{t("sales.tasks.assigneeMe")}</option>
              {assignees.candidates.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.name}
                </option>
              ))}
            </Select>
            {assigneeRefused ? (
              <span className="field-error" role="alert" data-testid="sales-task-error-assignee">
                {t("sales.tasks.assigneeNotEligible")}
              </span>
            ) : null}
          </Field>
        ) : null}
        <Field label={t("sales.tasks.noteLabel")} wide>
          <textarea
            value={draft.note}
            rows={3}
            data-testid="sales-task-note"
            aria-invalid={fieldError("note") !== undefined ? true : undefined}
            onChange={(e) => {
              setDraft({ ...draft, note: e.target.value });
            }}
          />
          {errorLine("note")}
        </Field>
      </div>

      {fixed ? (
        <p className="sales-hint" data-testid="sales-task-fixed-subject">
          {t("sales.tasks.subjectLabel")}: {prefill.label}
        </p>
      ) : (
        <div className="sales-form" data-testid="sales-task-subject">
          <Field label={t("sales.tasks.customerLabel")} wide>
            <input
              value={customerText}
              placeholder={t("sales.tasks.customerHint")}
              data-testid="sales-task-customer-q"
              onChange={(e) => {
                setCustomerText(e.target.value);
                // A new search abandons the chosen customer and its records:
                // a customer id left armed under a box that says something
                // else is the form lying about itself.
                setCustomerId("");
                setQuoteId("");
                setOrderId("");
                setOpportunityId("");
              }}
            />
          </Field>
          {customerQ.length > 1 ? (
            <Field label={t("sales.tasks.customerLabel")}>
              <Select
                value={customerId}
                data-testid="sales-task-customer"
                onChange={(e) => {
                  create.reset();
                  setCustomerId(e.target.value);
                  setQuoteId("");
                  setOrderId("");
                  setOpportunityId("");
                }}
              >
                <option value="">{t("sales.tasks.customerNone")}</option>
                {(customerMatches.data?.items ?? []).map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.display_name}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
          {pickedCustomer ? (
            <>
              {offerDeals ? (
                <Field label={t("sales.tasks.dealLabel")}>
                  <Select
                    value={opportunityId}
                    data-testid="sales-task-deal-pick"
                    onChange={(e) => {
                      create.reset();
                      setOpportunityId(e.target.value);
                    }}
                  >
                    <option value="">{t("sales.tasks.dealNone")}</option>
                    {(customerDeals.data?.items ?? []).map((deal) => (
                      <option key={deal.id} value={deal.id}>
                        {t("sales.composer.dealChip", {
                          no: deal.opportunity_no,
                          title: deal.title,
                        })}
                      </option>
                    ))}
                  </Select>
                </Field>
              ) : null}
              <Field label={t("sales.tasks.quoteLabel")}>
                <Select
                  value={quoteId}
                  data-testid="sales-task-quote-pick"
                  onChange={(e) => {
                    create.reset();
                    setQuoteId(e.target.value);
                  }}
                >
                  <option value="">{t("sales.tasks.documentNone")}</option>
                  {(customerQuotes.data?.items ?? []).map((quote) => (
                    <option key={quote.id} value={quote.id}>
                      {quote.quote_no}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t("sales.tasks.orderLabel")}>
                <Select
                  value={orderId}
                  data-testid="sales-task-order-pick"
                  onChange={(e) => {
                    create.reset();
                    setOrderId(e.target.value);
                  }}
                >
                  <option value="">{t("sales.tasks.documentNone")}</option>
                  {(customerOrders.data?.items ?? []).map((order) => (
                    <option key={order.id} value={order.id}>
                      {order.order_no}
                    </option>
                  ))}
                </Select>
              </Field>
            </>
          ) : null}
        </div>
      )}
      {subjectKey !== undefined ? (
        <p className="field-error" role="alert" data-testid="sales-task-error-subject">
          {t(subjectKey)}
        </p>
      ) : null}
      {/* A non-admin has no assignee field to hang the 422 on (they create
          for themselves), so it is said on the form rather than swallowed. */}
      {assigneeRefused && !perms.isAdmin ? (
        <p className="field-error" role="alert" data-testid="sales-task-error-assignee">
          {t("sales.tasks.assigneeNotEligible")}
        </p>
      ) : null}

      {otherFailure ? (
        <MutationFailureNote
          data-testid="sales-task-create-failed"
          text={t("sales.tasks.createFailed")}
          error={create.error}
        />
      ) : null}

      <div className="sales-modal-actions">
        <Button onClick={onClose} disabled={create.isPending}>
          {t("sales.common.cancel")}
        </Button>
        <Button
          variant="primary"
          data-testid="sales-task-submit"
          busy={create.isPending}
          busyLabel={t("sales.tasks.creating")}
          onClick={submit}
        >
          {t("sales.tasks.create")}
        </Button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// the change dialogs (E2-1, D-313 rulings 1 and 2)
// ---------------------------------------------------------------------------

/**
 * The PATCH body an Edit sends (pure, unit-tested): only the fields that
 * differ from the row as it stands, each normalised the way the create sends
 * it (the title trimmed, an empty note as `null`). An empty object means
 * nothing changed, and the dialog then sends nothing (D-313 ruling 1: a PATCH
 * whose every field is unchanged writes nothing anyway, so it is not worth a
 * request).
 */
export function taskEditBody(
  row: Pick<SalesActivity, "kind" | "title" | "note" | "due_on">,
  draft: TaskDraft,
): SalesActivityUpdateRequest {
  const title = draft.title.trim();
  const note = draft.note.trim();
  const storedNote = row.note ?? "";
  return {
    ...(draft.kind !== row.kind ? { kind: draft.kind } : {}),
    ...(title !== (row.title ?? "") ? { title } : {}),
    ...(note !== storedNote ? { note: note.length > 0 ? note : null } : {}),
    ...(draft.dueOn !== row.due_on ? { due_on: draft.dueOn } : {}),
  };
}

function DialogActions({
  onClose,
  pending,
  confirmLabel,
  busyLabel,
  confirmTestId,
  disabled,
  onConfirm,
  danger = false,
}: {
  readonly onClose: () => void;
  readonly pending: boolean;
  readonly confirmLabel: string;
  readonly busyLabel: string;
  readonly confirmTestId: string;
  readonly disabled: boolean;
  readonly onConfirm: () => void;
  readonly danger?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="sales-modal-actions">
      <Button onClick={onClose} disabled={pending}>
        {t("sales.common.cancel")}
      </Button>
      <Button
        variant={danger ? "danger" : "primary"}
        data-testid={confirmTestId}
        busy={pending}
        busyLabel={busyLabel}
        disabled={disabled}
        onClick={onConfirm}
      >
        {confirmLabel}
      </Button>
    </div>
  );
}

/** Move to another day: a new due date for an open task (the assignee's or a
 * literal admin's). The confirm stays off until the date differs. */
export function MoveTaskModal({
  row,
  onClose,
}: {
  readonly row: SalesActivity;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateActivity();
  const [dueOn, setDueOn] = useState(row.due_on);
  const valid = /^\d{4}-\d{2}-\d{2}$/u.test(dueOn);
  const serverDue =
    update.error instanceof RequestError && update.error.httpStatus === 400
      ? serverTaskFieldErrors(update.error.fieldErrors).due_on
      : undefined;
  const dueError = !valid ? "sales.tasks.dueRequired" : serverDue;
  return (
    <Modal
      heading={t("sales.tasks.moveTitle")}
      label={t("sales.tasks.moveTitle")}
      closeLabel={t("sales.common.close")}
      onClose={onClose}
      className="sales-modal"
      testId="sales-task-move-modal"
      pending={update.isPending}
    >
      <div className="sales-form">
        <p className="sales-hint" data-testid="sales-task-move-what">
          {taskWhat(t, row)}
        </p>
        <Field label={t("sales.tasks.moveDueLabel")}>
          <input
            type="date"
            value={dueOn}
            data-testid="sales-task-move-due"
            aria-invalid={dueError !== undefined ? true : undefined}
            onChange={(e) => {
              update.reset();
              setDueOn(e.target.value);
            }}
          />
          {dueError !== undefined ? (
            <span className="field-error" role="alert" data-testid="sales-task-move-error-due">
              {t(dueError)}
            </span>
          ) : null}
        </Field>
      </div>
      {update.isError && serverDue === undefined ? (
        <MutationFailureNote
          data-testid="sales-task-move-failed"
          text={t(changeFailureKey(update.error, "sales.tasks.moveFailed"))}
          error={update.error}
        />
      ) : null}
      <DialogActions
        onClose={onClose}
        pending={update.isPending}
        confirmLabel={t("sales.tasks.moveConfirm")}
        busyLabel={t("sales.tasks.moving")}
        confirmTestId="sales-task-move-confirm"
        disabled={!valid || dueOn === row.due_on}
        onConfirm={() => {
          update.mutate({ id: row.id, body: { due_on: dueOn } }, { onSuccess: onClose });
        }}
      />
    </Modal>
  );
}

/** Edit: a manual task's words (kind, title, note) and its due date, in one
 * PATCH naming only what changed. */
export function EditTaskModal({
  row,
  onClose,
}: {
  readonly row: SalesActivity;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateActivity();
  const [draft, setDraft] = useState<TaskDraft>({
    kind: row.kind,
    title: row.title ?? "",
    note: row.note ?? "",
    dueOn: row.due_on,
  });
  const clientErrors = validateTaskDraft(draft);
  const serverErrors =
    update.error instanceof RequestError && update.error.httpStatus === 400
      ? serverTaskFieldErrors(update.error.fieldErrors)
      : {};
  const fieldError = (field: TaskDraftField): string | undefined =>
    clientErrors[field] ?? serverErrors[field];
  const body = taskEditBody(row, draft);
  const changed = Object.keys(body).length > 0;
  const failureOnForm = Object.keys(serverErrors).length > 0;
  const errorLine = (field: TaskDraftField) => {
    const key = fieldError(field);
    return key === undefined ? null : (
      <span className="field-error" role="alert" data-testid={`sales-task-edit-error-${field}`}>
        {t(key)}
      </span>
    );
  };
  const set = (next: TaskDraft) => {
    update.reset();
    setDraft(next);
  };
  return (
    <Modal
      heading={t("sales.tasks.editTitle")}
      label={t("sales.tasks.editTitle")}
      closeLabel={t("sales.common.close")}
      onClose={onClose}
      className="sales-modal"
      testId="sales-task-edit-modal"
      pending={update.isPending}
    >
      <div className="sales-form">
        <Field label={t("sales.tasks.kindLabel")}>
          <Select
            value={draft.kind}
            data-testid="sales-task-edit-kind"
            onChange={(e) => {
              set({ ...draft, kind: e.target.value as SalesActivityKind });
            }}
          >
            {ACTIVITY_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {t(`sales.tasks.kind.${kind}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("sales.tasks.titleLabel")} wide>
          <input
            value={draft.title}
            data-testid="sales-task-edit-title"
            aria-invalid={fieldError("title") !== undefined ? true : undefined}
            onChange={(e) => {
              set({ ...draft, title: e.target.value });
            }}
          />
          {errorLine("title")}
        </Field>
        <Field label={t("sales.tasks.dueLabel")}>
          <input
            type="date"
            value={draft.dueOn}
            data-testid="sales-task-edit-due"
            aria-invalid={fieldError("due_on") !== undefined ? true : undefined}
            onChange={(e) => {
              set({ ...draft, dueOn: e.target.value });
            }}
          />
          {errorLine("due_on")}
        </Field>
        <Field label={t("sales.tasks.noteLabel")} wide>
          <textarea
            value={draft.note}
            rows={3}
            data-testid="sales-task-edit-note"
            aria-invalid={fieldError("note") !== undefined ? true : undefined}
            onChange={(e) => {
              set({ ...draft, note: e.target.value });
            }}
          />
          {errorLine("note")}
        </Field>
      </div>
      {update.isError && !failureOnForm ? (
        <MutationFailureNote
          data-testid="sales-task-edit-failed"
          text={t(changeFailureKey(update.error, "sales.tasks.editFailed"))}
          error={update.error}
        />
      ) : null}
      <DialogActions
        onClose={onClose}
        pending={update.isPending}
        confirmLabel={t("sales.tasks.editConfirm")}
        busyLabel={t("sales.tasks.editing")}
        confirmTestId="sales-task-edit-confirm"
        disabled={!changed || Object.keys(clientErrors).length > 0}
        onConfirm={() => {
          update.mutate({ id: row.id, body }, { onSuccess: onClose });
        }}
      />
    </Modal>
  );
}

/** Reassign: a literal admin hands an open task to another eligible member.
 * The current assignee is not offered, and the confirm stays off until a
 * member is chosen. */
export function ReassignTaskModal({
  row,
  onClose,
}: {
  readonly row: SalesActivity;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateActivity();
  const assignees = useAssigneeCandidates(true);
  const [assignee, setAssignee] = useState("");
  const choices = assignees.candidates.filter((member) => member.id !== row.assignee_user_id);
  const refused =
    update.error instanceof RequestError && update.error.code === "ACTIVITY_ASSIGNEE_NOT_ELIGIBLE";
  return (
    <Modal
      heading={t("sales.tasks.reassignTitle")}
      label={t("sales.tasks.reassignTitle")}
      closeLabel={t("sales.common.close")}
      onClose={onClose}
      className="sales-modal"
      testId="sales-task-reassign-modal"
      pending={update.isPending}
    >
      <div className="sales-form">
        <p className="sales-hint" data-testid="sales-task-reassign-what">
          {taskWhat(t, row)} · <TaskAssignee row={row} />
        </p>
        <Field label={t("sales.tasks.reassignLabel")}>
          <Select
            value={assignee}
            data-testid="sales-task-reassign-pick"
            aria-invalid={refused ? true : undefined}
            onChange={(e) => {
              update.reset();
              setAssignee(e.target.value);
            }}
          >
            <option value="" disabled>
              {assignees.isPending ? t("sales.common.loading") : ""}
            </option>
            {choices.map((member) => (
              <option key={member.id} value={member.id}>
                {member.name}
              </option>
            ))}
          </Select>
          {refused ? (
            <span className="field-error" role="alert" data-testid="sales-task-reassign-error">
              {t("sales.tasks.assigneeNotEligible")}
            </span>
          ) : null}
        </Field>
      </div>
      {update.isError && !refused ? (
        <MutationFailureNote
          data-testid="sales-task-reassign-failed"
          text={t(changeFailureKey(update.error, "sales.tasks.reassignFailed"))}
          error={update.error}
        />
      ) : null}
      <DialogActions
        onClose={onClose}
        pending={update.isPending}
        confirmLabel={t("sales.tasks.reassignConfirm")}
        busyLabel={t("sales.tasks.reassigning")}
        confirmTestId="sales-task-reassign-confirm"
        disabled={assignee.length === 0}
        onConfirm={() => {
          update.mutate(
            { id: row.id, body: { assignee_user_id: assignee } },
            { onSuccess: onClose },
          );
        }}
      />
    </Modal>
  );
}

/** Withdraw: a manual task leaves the open list as withdrawn; nothing is
 * deleted (D-313 ruling 2), and the dialog says so before the press. */
export function WithdrawTaskModal({
  row,
  onClose,
}: {
  readonly row: SalesActivity;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const withdraw = useWithdrawActivity();
  return (
    <Modal
      heading={t("sales.tasks.withdrawTitle")}
      label={t("sales.tasks.withdrawTitle")}
      closeLabel={t("sales.common.close")}
      onClose={onClose}
      className="sales-modal"
      testId="sales-task-withdraw-modal"
      pending={withdraw.isPending}
    >
      <div className="sales-form">
        <p data-testid="sales-task-withdraw-what">
          <strong>{taskWhat(t, row)}</strong>
        </p>
        <p className="sales-hint">{t("sales.tasks.withdrawBody")}</p>
      </div>
      {withdraw.isError ? (
        <MutationFailureNote
          data-testid="sales-task-withdraw-failed"
          text={t(withdrawFailureKey(withdraw.error))}
          error={withdraw.error}
        />
      ) : null}
      <DialogActions
        onClose={onClose}
        pending={withdraw.isPending}
        confirmLabel={t("sales.tasks.withdrawConfirm")}
        busyLabel={t("sales.tasks.withdrawing")}
        confirmTestId="sales-task-withdraw-confirm"
        disabled={false}
        danger
        onConfirm={() => {
          withdraw.mutate(row.id, { onSuccess: onClose });
        }}
      />
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// the row's verbs, shared by the list and the cards
// ---------------------------------------------------------------------------

/** The verbs a surface offers. The list offers all five; a card offers
 * Complete and Move to another day (spec 09 §4.4, §4.12, §4.15). */
export type TaskVerb = "complete" | "move" | "edit" | "reassign" | "withdraw";

export const ALL_TASK_VERBS: readonly TaskVerb[] = [
  "complete",
  "move",
  "edit",
  "reassign",
  "withdraw",
];
export const CARD_TASK_VERBS: readonly TaskVerb[] = ["complete", "move"];

interface OpenDialog {
  readonly verb: Exclude<TaskVerb, "complete">;
  readonly row: SalesActivity;
}

export interface TaskActionsController {
  /** The verbs the reader may press on this row, in button order (pure over
   * the reader, the row and whether the row is proven theirs). */
  readonly verbsFor: (row: SalesActivity, isOwn: boolean) => readonly TaskVerb[];
  readonly press: (verb: TaskVerb, row: SalesActivity) => void;
  readonly completingId: string | null;
  readonly completePending: boolean;
  /** The Complete refusal, said once under the list or the card. */
  readonly failure: ReactNode;
  /** The open dialog, if any. */
  readonly dialog: ReactNode;
}

/** Which verbs a reader may press on a row (pure, unit-tested): the offered
 * set, filtered by each verb's own gate. */
export function taskVerbsFor(
  reader: { readonly has: (code: string) => boolean; readonly isAdmin: boolean },
  row: SalesActivity,
  isOwn: boolean,
  offered: readonly TaskVerb[],
): TaskVerb[] {
  return offered.filter((verb) => {
    switch (verb) {
      case "complete":
        return canCompleteTask(reader, row, isOwn);
      case "move":
        return canMoveTask(reader, row, isOwn);
      case "edit":
        return canEditTaskWords(reader, row, isOwn);
      case "reassign":
        return canReassignTask(reader, row);
      case "withdraw":
        return canWithdrawTask(reader, row, isOwn);
    }
  });
}

/** One controller per surface: one Complete mutation (a second press waits
 * for the first), one dialog at a time, one failure line. */
export function useTaskActions(
  offered: readonly TaskVerb[],
  failureTestId: string,
): TaskActionsController {
  const { t } = useTranslation();
  const perms = useSalesPermissions();
  const complete = useCompleteActivity();
  const [completingId, setCompletingId] = useState<string | null>(null);
  const [open, setOpen] = useState<OpenDialog | null>(null);
  const close = () => {
    setOpen(null);
  };
  let dialog: ReactNode = null;
  if (open !== null) {
    switch (open.verb) {
      case "move":
        dialog = <MoveTaskModal key={open.row.id} row={open.row} onClose={close} />;
        break;
      case "edit":
        dialog = <EditTaskModal key={open.row.id} row={open.row} onClose={close} />;
        break;
      case "reassign":
        dialog = <ReassignTaskModal key={open.row.id} row={open.row} onClose={close} />;
        break;
      case "withdraw":
        dialog = <WithdrawTaskModal key={open.row.id} row={open.row} onClose={close} />;
        break;
    }
  }
  return {
    verbsFor: (row, isOwn) => taskVerbsFor(perms, row, isOwn, offered),
    press: (verb, row) => {
      complete.reset();
      if (verb === "complete") {
        setCompletingId(row.id);
        complete.mutate(row.id);
        return;
      }
      setOpen({ verb, row });
    },
    completingId,
    completePending: complete.isPending,
    failure: complete.isError ? (
      <MutationFailureNote
        data-testid={failureTestId}
        text={completeFailureText(t, complete.error)}
        error={complete.error}
      />
    ) : null,
    dialog,
  };
}

/**
 * The Done cell's editor (spec 09 §4.12, D-326 ruling 5) for one surface: a
 * `toggle` whose press sends `/complete`. It is writable on Complete's own
 * gate (`canCompleteTask`), and read-only, with its reason, on a done row, a
 * closed row, a row the reader cannot complete, and a row this cell has just
 * completed, so the press never reaches a reopen the route does not have.
 * `isOwn` is the surface's proof that a row is the reader's (hooks/
 * activity-queries.ts says why the client cannot compare ids). A 401 puts the
 * task back as not done and sends the reader to sign in (design-system
 * §3.13). Not done is a dash with its words for a screen reader, so a phone
 * card's Done label never stands over a blank.
 */
export function useDoneCellEditor(
  isOwn: (row: SalesActivity) => boolean,
  testId: string,
): ToggleCellEditor<SalesActivity> {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const perms = useSalesPermissions();
  const complete = useCompleteActivity();
  const [completed, setCompleted] = useState<ReadonlySet<string>>(new Set());
  const writable = (row: SalesActivity): boolean =>
    !completed.has(row.id) && canCompleteTask(perms, row, isOwn(row));
  return {
    kind: "toggle",
    testId,
    writable,
    reason: (row) =>
      writable(row)
        ? undefined
        : row.status === "done" || completed.has(row.id)
          ? t("sales.cells.done.isDone")
          : row.status === "cancelled"
            ? t("sales.cells.done.closed")
            : t("sales.cells.done.notAllowed"),
    editLabel: (row) => t("sales.cells.done.edit", { what: taskWhat(t, row) }),
    busyLabel: t("sales.common.saving"),
    failedLabel: t("sales.cells.done.failed"),
    value: (row) => row.status === "done",
    display: (done) =>
      done ? (
        <span data-testid="sales-task-done-value" data-done="true">
          {t("sales.cells.done.done")}
        </span>
      ) : (
        <span data-testid="sales-task-done-value" data-done="false">
          <span className="sales-hint" aria-hidden="true">
            —
          </span>
          <span className="visually-hidden">{t("sales.cells.done.notDone")}</span>
        </span>
      ),
    send: async (row, next) => {
      // One way: a cell that shows Done is read-only, so `next` is always
      // true here, and anything else is no write.
      if (!next) return { status: "settled" };
      try {
        await complete.mutateAsync(row.id);
        setCompleted((prior) => new Set(prior).add(row.id));
        return { status: "committed" };
      } catch (error) {
        if (error instanceof UnauthorizedError) {
          void navigate({ to: "/login" });
          return {
            status: "refused",
            code: "UNAUTHENTICATED",
            message: t("sales.cells.signedOut"),
          };
        }
        // A request that got no answer is the cell's own failure.
        if (!(error instanceof RequestError)) throw error;
        return {
          status: "refused",
          code: error.code ?? String(error.httpStatus),
          message: completeFailureText(t, error),
        };
      }
    },
    // `useCompleteActivity` re-reads every task read and the records that
    // carry a next step, refused or not.
    onSettled: () => undefined,
  };
}

const VERB_LABEL: Record<TaskVerb, string> = {
  complete: "sales.tasks.complete",
  move: "sales.tasks.move",
  edit: "sales.tasks.edit",
  reassign: "sales.tasks.reassign",
  withdraw: "sales.tasks.withdraw",
};

/** The row's buttons. Test ids are `<prefix>-<verb>`: `sales-task-complete`
 * on the list and `sales-doc-task-complete` on a card, as PR-E2 named them.
 *
 * D-560 ruling 4 (design-system §3.17 RowMenu): the row's first verb, which
 * is Complete wherever the reader may complete it, stays inline, and when two
 * or more verbs follow it they sit behind one "More actions for …" button
 * (`<prefix>-row-menu`), so an admin's row reads one button and a menu where
 * it read five buttons. A single follower stays inline beside the first, as
 * on the order and quote cards (Complete and Move to another day). */
export function TaskActionButtons({
  row,
  isOwn,
  actions,
  testIdPrefix,
}: {
  readonly row: SalesActivity;
  readonly isOwn: boolean;
  readonly actions: TaskActionsController;
  readonly testIdPrefix: string;
}) {
  const { t } = useTranslation();
  const verbs = actions.verbsFor(row, isOwn);
  if (verbs.length === 0) return null;
  const button = (verb: TaskVerb) =>
    verb === "complete" ? (
      <Button
        key={verb}
        size="sm"
        data-testid={`${testIdPrefix}-complete`}
        busy={actions.completePending && actions.completingId === row.id}
        busyLabel={t("sales.tasks.completing")}
        disabled={actions.completePending}
        onClick={() => {
          actions.press("complete", row);
        }}
      >
        {t("sales.tasks.complete")}
      </Button>
    ) : (
      <Button
        key={verb}
        size="sm"
        data-testid={`${testIdPrefix}-${verb}`}
        onClick={() => {
          actions.press(verb, row);
        }}
      >
        {t(VERB_LABEL[verb])}
      </Button>
    );
  const [first, ...rest] = verbs;
  return (
    <span className="sales-toolbar sales-task-actions ds-row-acts">
      {first !== undefined ? button(first) : null}
      {rest.length > 1 ? (
        <RowMenu
          label={t("nav.moreActionsFor", {
            name: row.title ?? t(`sales.tasks.kind.${row.kind}`),
          })}
          data-testid={`${testIdPrefix}-row-menu`}
        >
          {rest.map(button)}
        </RowMenu>
      ) : (
        rest.map(button)
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// the open-task card on the order, the quote, the customer and the deal
// ---------------------------------------------------------------------------

/** The card's empty sentence, by subject. */
const CARD_EMPTY: Record<TaskSubject["kind"], string> = {
  order: "sales.tasks.cardEmptyOrder",
  quote: "sales.tasks.cardEmptyQuote",
  customer: "sales.tasks.cardEmptyCustomer",
  opportunity: "sales.tasks.cardEmptyDeal",
};

/** The create form's fixed subject, from the card's subject. */
export function prefillOf(subject: TaskSubject, label: string): TaskPrefill {
  switch (subject.kind) {
    case "order":
      return { customerId: subject.customerId, salesOrderId: subject.id, label };
    case "quote":
      return { customerId: subject.customerId, quoteId: subject.id, label };
    case "customer":
      return { customerId: subject.id, label };
    case "opportunity":
      return { customerId: subject.customerId, opportunityId: subject.id, label };
  }
}

export interface OpenTasksCardProps {
  readonly subject: TaskSubject;
  /** The record's number or name, for the create form's fixed-subject line. */
  readonly label: string;
  /** The card's heading; `sales.tasks.cardTitle` ("Open tasks") by default. */
  readonly title?: string | undefined;
  /** A one-line failure in place of the error card, for a side panel whose
   * failure must not turn its page into an error screen (the 360). */
  readonly errorLine?: string | undefined;
  /** The empty sentence; the subject's own by default. */
  readonly emptyText?: string | undefined;
  /** The customer 360 also lists the latest five done tasks (spec 09 §4.2,
   * D-313 ruling 9). Meaningful for a customer subject only. */
  readonly recentDone?: boolean | undefined;
  /** Span the whole width of a grid of panels (the 360's Overview), where a
   * four-column table in a 280 px track would scroll inside itself. */
  readonly wide?: boolean | undefined;
  /** The verbs the card offers: Complete and Move to another day by default
   * (§4.4's task lines on the order and the composer); a side peek, whose
   * §4.15 line names Complete alone, passes `["complete"]`. */
  readonly verbs?: readonly TaskVerb[] | undefined;
  /** The Done cell (E2-2d, D-326 ruling 5) as the table's second column. On
   * by default where `recentDone` is, which is the customer 360's Tasks
   * panel, the one card spec 09 §4.12 names; the other cards keep Complete
   * among the verbs alone. */
  readonly doneCell?: boolean | undefined;
}

/**
 * The subject's OPEN tasks — the open-task line of D-292/1 — on the order
 * detail and the quote composer, and since E2-1 on the customer 360, the deal
 * page and each side peek, as a card of its own BELOW the record's tables,
 * never a column in them (the order's lines table is at its width limit in
 * Russian at 1440 px). Offered to the people tasks are for
 * (`sales.activity.write`: ga, fa, sales); a planner or finance reader of the
 * record gets no card, because the list would narrow to their own tasks and a
 * card saying "no open tasks" of a record that has some is a false answer.
 *
 * Complete and Move to another day are offered to a literal admin on every
 * open row, and to anyone else only on the rows the server returned as theirs.
 */
export function OpenTasksCard(props: OpenTasksCardProps) {
  const perms = useSalesPermissions();
  // The gate is a separate component so a reader without the code mounts NO
  // query and NO mutation at all — not a disabled one.
  if (!perms.has("sales.activity.write")) return null;
  return <OpenTasksCardBody {...props} />;
}

function OpenTasksCardBody({
  subject,
  label,
  title,
  errorLine,
  emptyText,
  recentDone = false,
  wide = false,
  verbs = CARD_TASK_VERBS,
  doneCell = recentDone,
}: OpenTasksCardProps) {
  const { t } = useTranslation();
  const perms = useSalesPermissions();
  // Every reader asks `all`: the subject's open tasks as far as they may
  // read them (the server narrows a sales manager to their own and their
  // assigned customers'). `mine` would show an admin only their own.
  const tasks = useDocumentTasks(subject, "all", true);
  // A non-admin may act only on their OWN tasks, and the client cannot
  // compare ids — so it asks the server which of these are theirs, under
  // `mine`. Until that answers, no row is theirs (fail closed).
  const own = useDocumentTasks(subject, "mine", !perms.isAdmin);
  const done = useRecentDoneTasks(subject.id, recentDone && subject.kind === "customer");
  const actions = useTaskActions(verbs, "sales-doc-task-complete-failed");
  const [adding, setAdding] = useState(false);
  const rows = useMemo(() => tasks.data?.items ?? [], [tasks.data]);
  const ownIds = useMemo(() => new Set((own.data?.items ?? []).map((row) => row.id)), [own.data]);
  const doneEditor = useDoneCellEditor((row) => ownIds.has(row.id), "sales-doc-task-done-cell");
  const heading = title ?? t("sales.tasks.cardTitle");
  const columns: DataTableColumn<SalesActivity>[] = [
    {
      key: "what",
      header: t("sales.tasks.colWhat"),
      id: true,
      render: (row) => <TaskWhat row={row} />,
    },
    ...(doneCell
      ? [{ key: "done", header: t("sales.cells.done.header"), editable: doneEditor }]
      : []),
    {
      key: "due_on",
      header: t("sales.tasks.colDue"),
      render: (row) => <TaskDue row={row} />,
    },
    {
      key: "assignee",
      header: t("sales.tasks.colAssignee"),
      render: (row) => <TaskAssignee row={row} />,
    },
    {
      key: "actions",
      header: t("sales.tasks.colActions"),
      actions: true,
      render: (row) => (
        <TaskActionButtons
          row={row}
          isOwn={ownIds.has(row.id)}
          actions={actions}
          testIdPrefix="sales-doc-task"
        />
      ),
    },
  ];
  const doneRows = done.data?.items ?? [];

  return (
    <Card
      className={wide ? "sales-360-card sales-360-wide" : "sales-360-card"}
      data-testid="sales-doc-tasks"
      title={heading}
      action={
        <Button
          size="sm"
          data-testid="sales-doc-task-add"
          onClick={() => {
            setAdding(true);
          }}
        >
          {t("sales.tasks.add")}
        </Button>
      }
    >
      {tasks.data === undefined ? (
        tasks.error !== null ? (
          errorLine !== undefined ? (
            <p className="sales-hint" data-testid="sales-doc-tasks-error">
              {errorLine}
            </p>
          ) : (
            <div data-testid="sales-doc-tasks-error">
              <ErrorState
                title={t("sales.tasks.cardError")}
                retryLabel={t("sales.common.retry")}
                requestIdLabel={t("sales.common.requestId")}
                requestId={requestIdOf(tasks.error)}
                onRetry={() => {
                  void tasks.refetch();
                }}
              />
            </div>
          )
        ) : tasks.fetchStatus === "paused" ? (
          // The read is held until the network returns (design-system §3.9):
          // said in the card's own register, one line where the page asked
          // for one-line failures, never a skeleton that does not end.
          errorLine !== undefined ? (
            <p className="sales-hint" role="status" data-testid="sales-doc-tasks-offline">
              {t("sales.offline.body")}
            </p>
          ) : (
            <div data-testid="sales-doc-tasks-offline">
              <OfflineState title={t("sales.offline.title")} body={t("sales.offline.body")} />
            </div>
          )
        ) : (
          <div data-testid="sales-doc-tasks-loading">
            <CardSkeleton lines={2} label={t("sales.common.loading")} />
          </div>
        )
      ) : (
        <>
          <DataTable
            data-testid="sales-doc-task-table"
            columns={columns}
            rows={rows}
            rowKey={(row) => row.id}
            caption={heading}
            rowTestId="sales-doc-task"
            rowProps={(row) => ({
              "data-task-id": row.id,
              "data-origin": row.origin,
              "data-reason": row.reason_code ?? undefined,
              "data-overdue": isTaskOverdue(row) ? "true" : "false",
            })}
            empty={
              <p className="sales-hint" data-testid="sales-doc-tasks-empty">
                {emptyText ?? t(CARD_EMPTY[subject.kind])}
              </p>
            }
          />
          {tasks.data.next_cursor !== null ? (
            <p className="sales-hint" data-testid="sales-doc-tasks-more">
              {t("sales.tasks.cardMore")}{" "}
              <Link to="/sales/tasks" className="ds-lnk">
                {t("sales.tasks.openList")}
              </Link>
            </p>
          ) : null}
        </>
      )}
      {recentDone && doneRows.length > 0 ? (
        <div data-testid="sales-doc-tasks-done">
          <h4>{t("sales.tasks.statusDone")}</h4>
          <ul className="sales-360-quote-list">
            {doneRows.map((row) => (
              <li key={row.id} data-testid="sales-doc-task-done" data-task-id={row.id}>
                <TaskWhat row={row} />
                <TaskClosedAt row={row} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {actions.failure}
      {actions.dialog}
      {adding ? (
        <NewTaskModal
          prefill={prefillOf(subject, label)}
          onClose={() => {
            setAdding(false);
          }}
        />
      ) : null}
    </Card>
  );
}
